import { Trans, useLingui } from "@lingui/react/macro";
import { patientKeys, updatePostDischargeProcessed } from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import Checkbox from "@/components/Checkbox/Checkbox";

export const EditPostDischargeProcessed = ({
  currentPostDischargeProcessed,
  onSuccess,
  patientId,
}: {
  currentPostDischargeProcessed: boolean | null | undefined;
  onSuccess: () => void;
  patientId: string;
}) => {
  const { t } = useLingui();
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      postDischargeProcessed: currentPostDischargeProcessed,
    },
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (postDischargeProcessed: boolean | null | undefined) =>
      updatePostDischargeProcessed(patientId, postDischargeProcessed),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        mutate(formData.postDischargeProcessed);
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar efterhantering`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : undefined}
          <Form.Row>
            <Checkbox
              label={{ text: t`Efterhanterad` }}
              {...register("postDischargeProcessed")}
            />
          </Form.Row>
          <FilledButton type="submit">
            <Trans>Spara ändringar</Trans>
          </FilledButton>
        </>
      )}
    </Form>
  );
};
